// rraRequests — RRA v2.2 (WEL-180): a case carries several REQUESTS, not animals.
//
// The pure logic behind the console's multi-request pieces, so it is testable without React or a
// fetch layer (type-only imports of the client, like rraGateDraft.ts):
//
//   • the New Request modal's request rows, their validation, and the create body;
//   • the "Add a request" panel's body and the notice staff read after it;
//   • the Item panel's words: the request as the title, "Request n" when there are several, the
//     "Covers: …" line, and which requests are still waiting for the requester;
//   • the request lines in the record's voice (moved here from the page so they are tested).
//
// No ids ever reach a string here — names, roles, kinds and dates only (docs/no-ids-in-ui.md).
//
//   cd apps/web && npx tsx --test src/lib/rraRequests.test.ts

import {
  OTHER_RULE_LABEL,
  PHYSICAL_CHANGE_LABEL,
  allowAnimalLabel,
  requestLabel,
  type RraCopySubject,
} from './rraCopy';
import type {
  AddRraRequestBody,
  AddRraRequestResponse,
  CreateRraCaseBody,
  RraApprovedAnimalView,
  RraCaseView,
  RraGateBody,
  RraItemView,
  RraScope,
} from './rraStaffClient';

// ---------------------------------------------------------------------------
// Kinds and limits
// ---------------------------------------------------------------------------

/** A case carries 1-10 requests (the API's cap). */
export const REQUESTS_MAX = 10;
/** The staff's one-line statement of a request (the API's cap). */
export const REQUEST_STATEMENT_MAX = 500;

/** The four kinds, in the order the modal offers them (labels as the console has always used). */
export const REQUEST_KINDS: ReadonlyArray<{ value: RraScope; label: string }> = [
  { value: 'esa_animal', label: 'Assistance animal (ESA)' },
  { value: 'service_animal', label: 'Trained service animal' },
  { value: 'other_accommodation', label: 'Other accommodation' },
  { value: 'modification', label: 'Physical modification' },
];

export const REQUEST_STATEMENT_LABEL = 'What are they asking us to change?';
/** An example, not a template: no animal details live here, and the household's "animal" wording stays. */
export const REQUEST_STATEMENT_PLACEHOLDER = 'e.g. an exemption to the animal rules for two dogs and a cat';
export const REQUEST_STATEMENT_REQUIRED = 'Say what they are asking us to change.';

/** An animal request: an ESA or a service animal. */
export function isAnimalScope(scope: string): boolean {
  return scope === 'esa_animal' || scope === 'service_animal';
}
export function isAnimalItem(item: { scope: string }): boolean {
  return isAnimalScope(item.scope);
}

/** The statement is required for a non-animal kind (nothing else says what it is); optional for an animal kind. */
export function statementRequired(scope: RraScope): boolean {
  return !isAnimalScope(scope);
}

// ---------------------------------------------------------------------------
// Request rows (New Request modal, Add a request panel)
// ---------------------------------------------------------------------------

/** One request being composed. `key` is a React key only. */
export interface RequestRow {
  key: string;
  scope: RraScope;
  statement: string;
}

let rowCounter = 0;
/** A fresh React key for a new row. */
export function nextRowKey(): string {
  rowCounter += 1;
  return `req-${rowCounter}`;
}

export function blankRequestRow(key: string = nextRowKey(), scope: RraScope = 'esa_animal'): RequestRow {
  return { key, scope, statement: '' };
}

/** What is wrong with one row, in words — or null. */
export function rowProblem(row: Pick<RequestRow, 'scope' | 'statement'>): string | null {
  if (statementRequired(row.scope) && !row.statement.trim()) return REQUEST_STATEMENT_REQUIRED;
  return null;
}

/** Per-row problems, same order as the rows. */
export function rowProblems(rows: ReadonlyArray<Pick<RequestRow, 'scope' | 'statement'>>): Array<string | null> {
  return rows.map(rowProblem);
}

export function rowsValid(rows: ReadonlyArray<Pick<RequestRow, 'scope' | 'statement'>>): boolean {
  return rows.length >= 1 && rows.length <= REQUESTS_MAX && rowProblems(rows).every((p) => p === null);
}

export function canAddRow(rows: ReadonlyArray<unknown>): boolean {
  return rows.length < REQUESTS_MAX;
}

/** The case-create `items` — kind and the staff's statement only (never an animal). */
export function itemsFromRows(rows: ReadonlyArray<Pick<RequestRow, 'scope' | 'statement'>>): CreateRraCaseBody['items'] {
  return rows.map((r) => {
    const statement = r.statement.trim();
    return { scope: r.scope, ...(statement ? { description: statement } : {}) };
  });
}

/**
 * The scope the case-level gate form is shown for. The gate is one set of answers for the whole case;
 * the trained-service-animal flag exists only for a service-animal request (the API applies it only
 * there), so the box must be offered whenever ANY request is a service animal.
 */
export function gateScopeFor(scopes: ReadonlyArray<RraScope>): RraScope {
  if (scopes.includes('service_animal')) return 'service_animal';
  return scopes[0] ?? 'other_accommodation';
}

/** The "Add a request" body: the one request and its own gate. */
export function addRequestBody(
  row: Pick<RequestRow, 'scope' | 'statement'>,
  gate: RraGateBody,
): AddRraRequestBody {
  const statement = row.statement.trim();
  return { scope: row.scope, ...(statement ? { description: statement } : {}), gate };
}

/** One attempt to add a request: what was sent (without its key) and the key it carried. */
export interface AddAttempt {
  fingerprint: string;
  key: string;
}

/** A fresh idempotency key (crypto.randomUUID where the browser has it; a random fallback otherwise). */
export function newClientRequestId(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (c?.randomUUID) return c.randomUUID();
  return `req-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/**
 * The key for this Add click. A NEW key for every distinct request; the SAME key when the staff member
 * presses Add again on exactly what the last attempt sent (a retry after an error or a lost reply), so
 * a request that did land is not added twice. Editing the request first makes it a new attempt.
 */
export function nextAddAttempt(
  prev: AddAttempt | null,
  body: AddRraRequestBody,
  make: () => string = newClientRequestId,
): AddAttempt {
  // JSON drops the undefined key, so the fingerprint is the body WITHOUT its idempotency key.
  const fingerprint = JSON.stringify({ ...body, clientRequestId: undefined });
  return prev && prev.fingerprint === fingerprint ? prev : { fingerprint, key: make() };
}

/** Why the "we added to your request" email did not go, in words (the machine reasons the send layer returns). */
export function emailReasonWords(reason: string): string {
  switch (reason) {
    case 'no_email':
      return 'there is no email on file';
    case 'test_recipient_blocked':
      return 'this is a test case and that address is not an allowed test address';
    case 'case_closed':
      return 'the case is closed';
    case 'send_failed':
      return 'the email could not be sent';
    default:
      return 'it could not be sent';
  }
}

/** The notice after "Add a request" — one of three, by what happened to the requester email. */
export function addRequestNotice(
  res: Pick<AddRraRequestResponse, 'requesterEmailed' | 'emailReason'> | undefined,
  requesterName: string | undefined,
): string {
  const who = requesterName?.trim() || 'the requester';
  if (res?.requesterEmailed) return `Added. We emailed ${who} to tell us about it.`;
  if (res?.emailReason) {
    return `Added, but the email didn't go (${emailReasonWords(res.emailReason)}) — use “Resend the request form”.`;
  }
  return "Added — send the request form when you're ready.";
}

// ---------------------------------------------------------------------------
// The case: which requests exist, which are still waiting
// ---------------------------------------------------------------------------

/** Item statuses that admit no further decision. */
const DECIDED_ITEM_STATUSES: ReadonlyArray<string> = ['approved', 'denied', 'withdrawn'];
/** Case statuses that are terminal (a dormant case is NOT closed). */
const CLOSED_CASE_STATUSES: ReadonlyArray<string> = ['decided', 'withdrawn'];

export function isOpenItem(item: Pick<RraItemView, 'status'>): boolean {
  return !DECIDED_ITEM_STATUSES.includes(item.status);
}

export function isCaseClosed(record: Pick<RraCaseView, 'status'>): boolean {
  return CLOSED_CASE_STATUSES.includes(record.status);
}

/**
 * Is this request still waiting for the requester? An OPEN request with no `request` recorded counts
 * only when the intake has not been submitted yet, OR the request was added AFTER the intake was
 * submitted (its `createdAt` is later than `submittedAt`). A request that already existed when an
 * older intake was submitted is answered even with no `request` on it — forms before v2.1 never
 * recorded one. The API applies the same rule, so the console and the requester form agree.
 */
export function isUnanswered(
  item: Pick<RraItemView, 'status' | 'request' | 'createdAt'>,
  submittedAt: string | undefined,
): boolean {
  if (!isOpenItem(item) || item.request) return false;
  if (!submittedAt) return true;
  return Date.parse(item.createdAt) > Date.parse(submittedAt);
}

/** The open requests still waiting for the requester (see `isUnanswered`; pass the intake's `submittedAt`). */
export function unansweredItems<T extends Pick<RraItemView, 'status' | 'request' | 'createdAt'>>(
  items: ReadonlyArray<T>,
  submittedAt: string | undefined,
): T[] {
  return items.filter((i) => isUnanswered(i, submittedAt));
}

/** The case is open and at least one request still waits for the requester. */
export function awaitingRequester(record: Pick<RraCaseView, 'status' | 'items'> & { intake: Pick<RraCaseView['intake'], 'submittedAt'> }): boolean {
  return !isCaseClosed(record) && unansweredItems(record.items, record.intake.submittedAt).length > 0;
}

/** "Add a request" is offered on an open case that was not born approved at lease build. */
export function canAddRequest(record: Pick<RraCaseView, 'status' | 'origin'>): boolean {
  return !isCaseClosed(record) && record.origin !== 'lease_build';
}

/** "Request 2" when the case carries several requests; null for a lone request. Positions follow the stored order. */
export function itemOrdinalLabel(
  record: Pick<RraCaseView, 'items'>,
  item: Pick<RraItemView, 'itemId'>,
): string | null {
  if (record.items.length <= 1) return null;
  const idx = record.items.findIndex((i) => i.itemId === item.itemId);
  return idx < 0 ? null : requestLabel(idx + 1);
}

// ---------------------------------------------------------------------------
// One request's words
// ---------------------------------------------------------------------------

/** The voice the record's request lines are in (B2): the household member's name, else the requester themselves. */
export function requestVoice(record: Pick<RraCaseView, 'permission'>): RraCopySubject {
  const subject = record.permission.subject;
  return subject && subject.kind === 'household_member'
    ? { readerIsSubject: false, name: subject.name, minor: subject.minor }
    : { readerIsSubject: true, name: '', minor: false };
}

/** RRA v2.1 (B3): the set request fields as the staff-readable bullets, in the record's voice (same words as the request copy). */
export function requestBullets(
  request: NonNullable<RraItemView['request']>,
  voice: RraCopySubject,
  isAnimal: boolean,
): string[] {
  const out: string[] = [];
  if (request.allowAnimal && isAnimal) out.push(allowAnimalLabel(voice));
  if (request.otherRule) out.push(`${OTHER_RULE_LABEL}: ${request.otherRule}`);
  if (request.physicalChange) out.push(`${PHYSICAL_CHANGE_LABEL}: ${request.physicalChange}`);
  return out;
}

/** Shown when nothing else names the request, so the title is never blank. */
const SCOPE_REQUEST_WORDS: Record<RraScope, string> = {
  esa_animal: 'Assistance animal request',
  service_animal: 'Service animal request',
  other_accommodation: 'Accommodation request',
  modification: 'Modification request',
};

/** A legacy item's one-line animal title: name · kind · breed. */
function legacyAnimalTitle(item: RraItemView): string {
  const a = item.animal;
  if (!a) return '';
  return [a.name, a.type === 'other' ? a.otherDescription : a.type, a.breed].filter(Boolean).join(' · ');
}

/**
 * The item panel's title. A legacy item (staff typed the animal) keeps its animal line. Any other
 * request is titled by what it asks: the staff's statement, else the recorded request in the
 * record's voice, else a plain word for its kind.
 */
export function itemTitle(item: RraItemView, voice: RraCopySubject): string {
  if (item.animal) return legacyAnimalTitle(item) || SCOPE_REQUEST_WORDS[item.scope];
  const statement = item.description?.trim();
  if (statement) return statement;
  if (item.request) {
    const bullets = requestBullets(item.request, voice, isAnimalItem(item));
    if (bullets.length > 0) return bullets.join(' · ');
  }
  return SCOPE_REQUEST_WORDS[item.scope];
}

/** One covered animal in words: `Rex (dog)`, `Mittens (cat, tabby)`, or just the name. */
export function animalWords(a: Pick<RraApprovedAnimalView, 'name' | 'type' | 'breed'>): string {
  const name = a.name?.trim() || 'Unnamed animal';
  const bits = [a.type, a.breed?.trim()].filter(Boolean);
  return bits.length > 0 ? `${name} (${bits.join(', ')})` : name;
}

/** `Covers: Rex (dog), Mittens (cat)` — or null when the approval names no animals. */
export function coversLine(item: Pick<RraItemView, 'approvedAnimals'>): string | null {
  const animals = item.approvedAnimals ?? [];
  if (animals.length === 0) return null;
  return `Covers: ${animals.map(animalWords).join(', ')}`;
}
